import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Rating } from '@material-ui/lab';
import { greys, onwardNavy } from '@/styles/colors';
import { SURVEY_TYPES } from '@/components/Account/Surveys/constants';
import { FONT, Empty, Card, KV } from './detailTabBlocks';

// Redesigned Survey tab. Preserves the grouping (PREDELIVERY / DRIVER / POSTDELIVERY,
// each merged with its *_DEFAULTS), numbered question/answer rendering (string / Yes-No /
// star rating), and the PREDELIVERY extras (Instructions, Gate Code) from the shared
// SurveyInfo + SurveyCard components.

function renderAnswer(response) {
    const question = response?.survey_question || {};
    switch (question.response_type) {
        case 'string':
            return (
                <span
                    css={css`
                        font-weight: 600;
                        color: ${greys[3]};
                    `}
                >
                    {response.response_string || '—'}
                </span>
            );
        case 'boolean':
            return (
                <span
                    css={css`
                        font-weight: 700;
                        color: ${greys[3]};
                    `}
                >
                    {response.response_boolean ? 'Yes' : 'No'}
                </span>
            );
        case 'number':
            return <Rating readOnly size="small" precision={0.5} value={response.response_number || 0} />;
        default:
            return null;
    }
}

function SurveyGroup({ surveyType, responses, order }) {
    if (!responses?.length) return null;

    return (
        <Card css="margin-bottom: 12px;">
            <div
                css={css`
                    font-family: ${FONT};
                    font-weight: 800;
                    font-size: 11px;
                    color: ${onwardNavy};
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    margin-bottom: 8px;
                `}
            >
                {SURVEY_TYPES[surveyType]} Survey
            </div>

            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                `}
            >
                {responses.map((response, idx) => (
                    <div
                        key={response.survey_id || idx}
                        css={css`
                            padding: 8px 0;
                            ${idx > 0 ? 'border-top: 1px solid #f2f2f2;' : ''}
                        `}
                    >
                        <div
                            css={css`
                                font-family: ${FONT};
                                font-weight: 600;
                                font-size: 12px;
                                color: ${greys[2]};
                                line-height: 1.4;
                                margin-bottom: 4px;
                            `}
                        >
                            {idx + 1}. {response.survey_question.question}
                        </div>
                        <div
                            css={css`
                                font-family: ${FONT};
                                font-size: 12.5px;
                            `}
                        >
                            {renderAnswer(response)}
                        </div>
                    </div>
                ))}
            </div>

            {surveyType === 'PREDELIVERY' ? (
                <div
                    css={css`
                        margin-top: 6px;
                        border-top: 1px solid #f2f2f2;
                    `}
                >
                    <KV label="Instructions" value={order?.customer_instructions || '—'} />
                    <KV label="Gate Code" value={order?.gate_code || '—'} />
                </div>
            ) : null}
        </Card>
    );
}

export default function DetailTabSurvey({ order }) {
    const surveyResponses = useMemo(() => {
        const responsesByType = (order?.survey_responses || []).reduce((acc, response) => {
            const type = response.survey_question.survey_type;
            return {
                ...acc,
                [type]: [...(acc[type] || []), response],
            };
        }, {});
        return {
            PREDELIVERY: [...(responsesByType.PREDELIVERY || []), ...(responsesByType.PREDELIVERY_DEFAULTS || [])],
            DRIVER: [...(responsesByType.DRIVER || []), ...(responsesByType.DRIVER_DEFAULTS || [])],
            POSTDELIVERY: [...(responsesByType.POSTDELIVERY || []), ...(responsesByType.POSTDELIVERY_DEFAULTS || [])],
        };
    }, [order]);

    const isEmpty = Object.values(surveyResponses).every((i) => !i?.length);
    if (isEmpty) {
        return <Empty top>No survey questions.</Empty>;
    }

    return (
        <div
            css={css`
                padding-top: 4px;
            `}
        >
            {Object.entries(surveyResponses).map(([surveyType, responses]) => (
                <SurveyGroup key={surveyType} surveyType={surveyType} responses={responses} order={order} />
            ))}
        </div>
    );
}
